/* Roadhouse product gallery and full-screen viewer (snippets/rh-product-gallery.liquid).
 * Stage: a scroll-snap track (swipe on phones), thumbnails and the arrow pill keep in step.
 * Viewer: <dialog> with zoom – tap/click toggles 2.5x at the point, drag pans, two fingers pinch,
 * the wheel zooms, arrows and swipe change the photo, Esc closes. */
(() => {
  'use strict';
  if (customElements.get('rh-gallery')) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  class RhGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = '1';
      this.track = this.querySelector('[data-rg-track]');
      if (!this.track) return;
      this.slides = [...this.querySelectorAll('[data-rg-slide]')];
      // Further photos (snippets/rh-img.liquid, defer) get their address once the page has loaded, so they do not
      // compete with the first photo on a slow phone connection – at once when the shopper touches the gallery first.
      const hydrate = () => {
        this.querySelectorAll('img[data-rh-defer]').forEach((img) => {
          img.srcset = img.dataset.srcset;
          img.src = img.dataset.src;
          img.removeAttribute('data-rh-defer');
        });
      };
      if (document.readyState === 'complete') hydrate();
      else window.addEventListener('load', hydrate, { once: true });
      ['pointerdown', 'touchstart', 'keydown', 'focusin'].forEach((ev) => this.addEventListener(ev, hydrate, { once: true, passive: true }));
      this.track.addEventListener('scroll', hydrate, { once: true, passive: true });
      if (this.dataset.startMedia) hydrate();
      this.thumbs = [...this.querySelectorAll('[data-rg-thumb]')];
      this.countEl = this.querySelector('[data-rg-count]');
      this.index = 0;
      const prev = this.querySelector('[data-rg-prev]'), next = this.querySelector('[data-rg-next]');
      prev?.addEventListener('click', () => this.go(this.index - 1));
      next?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(+t.dataset.rgThumb)));
      let raf = 0;
      this.track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => this.sync()); }, { passive: true });
      this.addEventListener('keydown', (e) => {
        if (e.target.closest('dialog')) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      });
      this.viewer = this.querySelector('[data-rg-viewer]') ? new Viewer(this.querySelector('[data-rg-viewer]')) : null;
      this.querySelectorAll('[data-rg-open]').forEach((b) => b.addEventListener('click', () => this.viewer?.open(+b.dataset.rgOpen, b)));
      this.querySelector('[data-rg-open-current]')?.addEventListener('click', (e) => {
        const btn = this.slides[this.index]?.querySelector('[data-rg-open]');
        this.viewer?.open(btn ? +btn.dataset.rgOpen : 0, e.currentTarget);
      });
      this.update();
      // A variant with its own photo (a colour of a shirt): show that photo – on load from ?variant=, and whenever
      // the shopper picks another variant (Dawn publishes PUB_SUB_EVENTS.variantChange with the variant).
      if (this.dataset.startMedia) requestAnimationFrame(() => this.showMedia(this.dataset.startMedia, true));
      const listen = () => {
        if (this.unsubscribe || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const media = event && event.data && event.data.variant && event.data.variant.featured_media;
          if (media) this.showMedia(media.id);
        });
      };
      listen();
      if (!this.unsubscribe) document.addEventListener('DOMContentLoaded', listen, { once: true });
    }
    disconnectedCallback() {
      if (this.unsubscribe) { this.unsubscribe(); this.unsubscribe = null; }
    }
    showMedia(id, instant) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i >= 0 && i !== this.index) this.go(i, instant);
    }
    go(i, instant) {
      const n = this.slides.length;
      i = (i + n) % n;
      this.track.scrollTo({ left: this.slides[i].offsetLeft - this.track.offsetLeft, behavior: reduce || instant ? 'auto' : 'smooth' });
      this.index = i;
      this.update();
    }
    sync() {
      const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
      if (i !== this.index && i >= 0 && i < this.slides.length) { this.index = i; this.update(); }
    }
    update() {
      this.slides.forEach((s, j) => {
        const on = j === this.index;
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (!on) s.querySelectorAll('video').forEach((v) => v.pause());
      });
      this.thumbs.forEach((t, j) => {
        const on = j === this.index;
        t.classList.toggle('is-active', on);
        if (on) { t.setAttribute('aria-current', 'true'); t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduce ? 'auto' : 'smooth' }); }
        else t.removeAttribute('aria-current');
      });
      if (this.countEl) this.countEl.textContent = `${this.index + 1} / ${this.slides.length}`;
    }
  }

  class Viewer {
    constructor(dialog) {
      this.d = dialog;
      this.stage = dialog.querySelector('[data-rv-stage]');
      this.img = dialog.querySelector('[data-rv-img]');
      this.items = [...dialog.querySelectorAll('[data-rv-thumb]')];
      this.countEl = dialog.querySelector('[data-rv-count]');
      this.hint = dialog.querySelector('[data-rv-hint]');
      this.i = 0; this.s = 1; this.x = 0; this.y = 0;
      this.pointers = new Map();
      dialog.querySelector('[data-rv-close]').addEventListener('click', () => this.close());
      dialog.querySelector('[data-rv-prev]').addEventListener('click', () => this.show(this.i - 1));
      dialog.querySelector('[data-rv-next]').addEventListener('click', () => this.show(this.i + 1));
      this.items.forEach((b) => b.addEventListener('click', () => this.show(+b.dataset.rvThumb)));
      dialog.addEventListener('close', () => { this.reset(); document.documentElement.classList.remove('rh-viewer-open'); });
      dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.show(this.i + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); this.show(this.i - 1); }
        else if (e.key === '+' || e.key === '=') this.zoomAt(this.s * 1.5);
        else if (e.key === '-') this.zoomAt(this.s / 1.5);
        else if (e.key === '0') this.reset();
      });
      this.bindGestures();
      addEventListener('resize', () => this.clamp(true));
    }
    open(i, opener) {
      this.opener = opener;
      this.d.querySelectorAll('img[loading="lazy"]').forEach((im) => { im.loading = 'eager'; });
      document.documentElement.classList.add('rh-viewer-open');
      if (typeof this.d.showModal === 'function') this.d.showModal(); else this.d.setAttribute('open', '');
      this.show(i);
    }
    close() { if (this.d.open) this.d.close(); this.opener?.focus({ preventScroll: true }); }
    show(i) {
      const n = this.items.length;
      this.i = (i + n) % n;
      const it = this.items[this.i];
      this.reset();
      // show the photo the stage already loaded at once, then swap in the sharp one when it arrives
      const stageImg = this.d.closest('rh-gallery')?.querySelectorAll('[data-rg-open] img')[this.i];
      const quick = stageImg?.currentSrc || '';
      const want = it.dataset.full;
      this.img.alt = it.dataset.alt || '';
      this.img.classList.toggle('is-loading', !quick);
      this.img.src = quick || want;
      if (quick && quick !== want) {
        const full = new Image();
        full.onload = () => { if (this.items[this.i] === it) this.img.src = want; };
        full.src = want;
      }
      this.img.onload = () => { this.img.classList.remove('is-loading'); };
      this.items.forEach((b, j) => { b.classList.toggle('is-active', j === this.i); if (j === this.i) b.scrollIntoView({ block: 'nearest', inline: 'center' }); });
      if (this.countEl) this.countEl.textContent = `${this.d.dataset.tPhoto || 'Foto'} ${this.i + 1} / ${n}`;
      // preload the neighbours
      [this.i + 1, this.i - 1].forEach((k) => { const nb = this.items[(k + n) % n]; if (nb) { const im = new Image(); im.src = nb.dataset.full; } });
    }
    apply(animate) {
      this.img.style.transition = animate && !reduce ? 'transform var(--rh-base, 220ms) var(--rh-ease, ease)' : 'none';
      this.img.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.s})`;
      this.d.classList.toggle('is-zoomed', this.s > 1.01);
    }
    reset() { this.s = 1; this.x = 0; this.y = 0; this.apply(true); }
    bounds() {
      const st = this.stage.getBoundingClientRect();
      const w = this.img.offsetWidth * this.s, h = this.img.offsetHeight * this.s;
      return { mx: Math.max(0, (w - st.width) / 2), my: Math.max(0, (h - st.height) / 2) };
    }
    clamp(animate) {
      const { mx, my } = this.bounds();
      this.x = Math.min(mx, Math.max(-mx, this.x));
      this.y = Math.min(my, Math.max(-my, this.y));
      this.apply(animate);
    }
    /* zoom so that the point (px, py) – relative to the stage centre – stays under the finger */
    zoomAt(s2, px = 0, py = 0, animate = true) {
      s2 = Math.min(4, Math.max(1, s2));
      const k = s2 / this.s;
      this.x = px - (px - this.x) * k;
      this.y = py - (py - this.y) * k;
      this.s = s2;
      if (s2 === 1) { this.x = 0; this.y = 0; }
      this.clamp(animate);
      if (this.hint) this.hint.hidden = true;
    }
    rel(e) { const r = this.stage.getBoundingClientRect(); return [e.clientX - r.left - r.width / 2, e.clientY - r.top - r.height / 2]; }
    bindGestures() {
      const st = this.stage;
      let start = null, moved = false, pinch = null, swipe = null, onImg = false;
      st.addEventListener('pointerdown', (e) => {
        if (e.target.closest('button')) return;
        if (this.pointers.size === 0) onImg = e.target === this.img;
        this.pointers.set(e.pointerId, [e.clientX, e.clientY]);
        try { st.setPointerCapture(e.pointerId); } catch { /* pointer already gone – the gesture still works without capture */ }
        moved = false;
        if (this.pointers.size === 2) {
          const [a, b] = [...this.pointers.values()];
          pinch = { d: Math.hypot(a[0] - b[0], a[1] - b[1]), s: this.s, mid: this.rel({ clientX: (a[0] + b[0]) / 2, clientY: (a[1] + b[1]) / 2 }) };
        } else {
          start = { cx: e.clientX, cy: e.clientY, x: this.x, y: this.y };
          swipe = { x: e.clientX, y: e.clientY, t: Date.now() };
        }
      });
      st.addEventListener('pointermove', (e) => {
        if (!this.pointers.has(e.pointerId)) return;
        this.pointers.set(e.pointerId, [e.clientX, e.clientY]);
        if (pinch && this.pointers.size === 2) {
          const [a, b] = [...this.pointers.values()];
          const d = Math.hypot(a[0] - b[0], a[1] - b[1]);
          this.zoomAt(pinch.s * (d / pinch.d), pinch.mid[0], pinch.mid[1], false);
          moved = true; return;
        }
        if (!start) return;
        const dx = e.clientX - start.cx, dy = e.clientY - start.cy;
        if (Math.abs(dx) + Math.abs(dy) > 6) moved = true;
        if (this.s > 1) { this.x = start.x + dx; this.y = start.y + dy; this.clamp(false); }
      });
      const end = (e) => {
        if (!this.pointers.has(e.pointerId)) return;
        this.pointers.delete(e.pointerId);
        if (pinch) { if (this.pointers.size < 2) pinch = null; start = null; return; }
        if (!moved && start && e.type === 'pointerup') {
          // tap on the photo: zoom in at the point, or back to the whole photo; beside it: close
          if (this.s > 1) this.reset();
          else if (onImg) { const [px, py] = this.rel(e); this.zoomAt(2.5, px, py); }
          else this.close();
        } else if (moved && this.s === 1 && swipe) {
          const dx = e.clientX - swipe.x;
          if (Math.abs(dx) > 60 && Date.now() - swipe.t < 600) this.show(this.i + (dx < 0 ? 1 : -1));
        }
        start = null; swipe = null;
      };
      st.addEventListener('pointerup', end);
      st.addEventListener('pointercancel', end);
      st.addEventListener('wheel', (e) => {
        e.preventDefault();
        const [px, py] = this.rel(e);
        this.zoomAt(this.s * Math.exp(-e.deltaY * 0.0022), px, py, false);
      }, { passive: false });
    }
  }

  customElements.define('rh-gallery', RhGallery);
})();
